import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  AreaChart,
  PieChart,
  ScatterChart,
  Bar,
  Line,
  Area,
  Pie,
  Cell,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE } from "./blockSurface";
import {
  formatAxisTick,
  formatChartValue,
  resolveChartSpec,
  truncateLabel,
  type ChartBlockProps,
  type ChartSpec,
} from "./chartSpec";

/**
 * Chart - themed chart for the K2 `chart` block
 * (`docs/contracts/block-registry.md`). Renders every BE#2577 chart family -
 * `bar` (vertical / horizontal / grouped / stacked), `line`, `area`
 * (stackable), `pie` (donut-able), and `scatter` - with recharts (the K2
 * chart decision + the existing FE `chart.tsx` both pin recharts, not vega).
 * Plot semantics (formatting, palette resolution, dense-label handling,
 * per-family recharts wiring) are PORTED from AIStudio-v3's production
 * `ChartBlock.tsx` / `chart-format.ts` so consumers can converge on this
 * package renderer without a visible change.
 *
 * `spec` accepts EITHER shape (see `chartSpec.ts`, the normalization seam):
 *
 * - **The flat BE#2577 block props** (`ChartBlockProps`) - what the runtime
 *   emits and the BE validates/persists (`chart_block.py`): `chart_type`,
 *   `title`, `subtitle?`, `x: {key, label?, type?}`, `series: [{key, label?,
 *   color?}]`, `data`, `value_format`, `summary`, `orientation?`, `stacked?`,
 *   `source_note?`. This is the PRIMARY shape; `BlockRenderer`'s `ChartBlock`
 *   routes a streamed chart block's props here directly.
 * - **The legacy nested `ChartSpec`** - `type` / `data` / `xKey` / `series` /
 *   `colors?` / `showLegend?` / `showGrid?` / `title?` / `description?`, kept
 *   backward-compatible, plus the newer optional variant fields (`xType` /
 *   `orientation` / `stacked` / `donut` / `valueFormat` / `summary` /
 *   `sourceNote`) so both shapes carry the same capabilities.
 *
 * Contract-field behavior:
 *   - `value_format` drives the value-axis ticks + tooltip values
 *     (`formatChartValue` / the compact `formatAxisTick`): `integer`
 *     (grouped, no decimals), `float` (<= 2 decimals), `currency` (**USD
 *     assumed** - the contract carries no currency code), `percent` (values
 *     are treated as ALREADY percent units - `42.5` -> `42.5%` - the shipped
 *     v3 convention; neither `chart_block.py` nor `render_chart.py` pins a
 *     0-1 scale). Legacy specs without `valueFormat` keep raw recharts ticks.
 *   - `summary` (required by the BE contract) is exposed to screen readers as
 *     the plot region's `role="img"` + `aria-label`, and shown as text in the
 *     empty/degraded state - the contract's graceful-fallback text.
 *   - `subtitle` renders in the existing description slot; `source_note` is a
 *     small muted footer line.
 *   - `orientation: "horizontal"` (bar) swaps to a horizontal bar chart;
 *     `stacked: true` stacks a multi-series bar/area (unstacked multi-series
 *     bars render grouped side-by-side).
 *   - `donut: true` renders a pie with a hole (~60% of the outer radius).
 *     NOTE: `donut` is a renderer-side extension - the BE's normalization
 *     strips unknown props ("the contract is closed"), so it CANNOT reach
 *     this renderer through the chat pipeline today; it works for
 *     host-constructed blocks and direct `<Chart>` consumers only, until the
 *     BE contract carries it.
 *   - `pie` plots the FIRST series (lenient on multi-series, matching the
 *     BE's generic validation), sliced by the `x.key` label per row.
 *   - `x.type` is load-bearing for `scatter` (`number` / `time` -> a numeric
 *     x-axis, else category); `time` / `number` x-values on the other
 *     families render as ordered categories.
 *   - an UNKNOWN `chart_type` (e.g. a future contract family reaching an old
 *     client) degrades to the summary text, never a misrepresenting default
 *     chart.
 *
 * Client-only props (never part of the streamed block): `className`,
 * `height`, `loading`, `error`, `locale`.
 *
 * Theming is token-driven: series default to `hsl(var(--chart-N))`, and the
 * axes / grid / tooltip / pie-slice separators read `--border`,
 * `--muted-foreground`, `--popover`, `--card`, etc. - no hardcoded colors.
 * Consumers load `--chart-*` via `@aisquare/ui/tokens/shadcn-extras.css`
 * (they degrade to the CSS-var fallbacks below if absent). Empty `data` /
 * `series` - and any junk payload - renders a muted empty state instead of a
 * blank canvas or a crash, satisfying the registry's never-crash-on-a-bad-
 * block contract. SSR-safe: recharts renders into a `ResponsiveContainer`
 * and touches the DOM only after mount.
 */

const chartVariants = cva(cn("flex w-full min-w-[18rem] flex-col gap-3", BLOCK_SURFACE));

export type {
  ChartBlockProps,
  ChartOrientation,
  ChartSeries,
  ChartSpec,
  ChartType,
  ChartValueFormat,
  ChartXAxis,
  ChartXAxisType,
  ResolvedChartSeries,
  ResolvedChartSpec,
} from "./chartSpec";
export {
  formatAxisTick,
  formatChartValue,
  isFlatChartProps,
  resolveChartSpec,
  truncateLabel,
} from "./chartSpec";

export interface ChartProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof chartVariants> {
  /**
   * The chart payload - the flat BE#2577 block props (`ChartBlockProps`, the
   * primary shape) or the legacy nested `ChartSpec`. See the JSDoc above.
   */
  spec: ChartSpec | ChartBlockProps;
  /** Plot height in px. Default `240`. */
  height?: number;
  /** Render a loading skeleton instead of content. */
  loading?: boolean;
  /** Render an error message instead of content. Pass `true` or a string. */
  error?: boolean | string;
  /** BCP-47 locale for value formatting. Defaults to the host's locale. */
  locale?: string;
}

// Brand chart tokens (see src/tokens/shadcn-extras.css). HSL triplets, so they
// are wrapped in hsl(); the second arg is a sensible fallback when an app
// hasn't loaded shadcn-extras.css. Five slots, cycled - matches v3's
// CHART_PALETTE_SIZE.
const DEFAULT_PALETTE = [
  "hsl(var(--chart-1, 213 80% 55%))",
  "hsl(var(--chart-2, 158 64% 42%))",
  "hsl(var(--chart-3, 33 90% 55%))",
  "hsl(var(--chart-4, 285 60% 55%))",
  "hsl(var(--chart-5, 350 75% 58%))",
];

const AXIS_COLOR = "hsl(var(--muted-foreground))";
const GRID_COLOR = "hsl(var(--border))";
// Pie-slice separator - matches the card surface the block sits on (recharts'
// default is hardcoded white, which clashes in dark mode; deliberate
// token-driven deviation from v3, which keeps the recharts default).
const SLICE_STROKE = "hsl(var(--card))";

/** Beyond this many x-categories, tilt the labels so they stop colliding. */
const DENSE_LABEL_THRESHOLD = 8;
/** Beyond this many rows the plot gets a min-width and scrolls horizontally. */
const SCROLL_ROW_THRESHOLD = 12;

// Same host-locale resolution the Stat widget uses: read `navigator` only at
// render on the client; SSR falls back to the runtime default locale so
// output is deterministic across the hydration boundary.
function resolveLocale(locale?: string): string | undefined {
  if (locale) return locale;
  if (typeof navigator !== "undefined") return navigator.language;
  return undefined;
}

export const Chart = React.forwardRef<HTMLDivElement, ChartProps>(function Chart(
  { className, spec, height = 240, loading = false, error = false, locale, ...rest },
  ref,
) {
  const resolved = React.useMemo(() => resolveChartSpec(spec), [spec]);
  const {
    type,
    data,
    xKey,
    xLabel,
    xType,
    series,
    colors,
    showLegend,
    showGrid,
    title,
    description,
    summary,
    sourceNote,
    orientation,
    stacked,
    donut,
    valueFormat,
  } = resolved;

  const palette = colors?.length ? colors : DEFAULT_PALETTE;
  const seriesColors = React.useMemo(
    () => series.map((s, i) => s.color ?? palette[i % palette.length]),
    [series, palette],
  );

  const resolvedLocale = resolveLocale(locale);

  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(chartVariants(), className)}
        aria-busy="true"
        data-slot="chart"
        {...rest}
      >
        <div className="h-3 w-32 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        <div
          className="w-full animate-pulse rounded-lg bg-muted motion-reduce:animate-none"
          style={{ height }}
        />
      </div>
    );
  }

  if (error) {
    const message = typeof error === "string" ? error : "Couldn't load this chart.";
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          chartVariants(),
          "border-destructive/30 bg-destructive/5 text-sm text-destructive",
          className,
        )}
        data-slot="chart"
        {...rest}
      >
        {message}
      </div>
    );
  }

  const renderable = type !== undefined && data.length > 0 && series.length > 0;

  const header =
    title || description ? (
      <div className="flex flex-col gap-0.5">
        {title ? <h3 className="text-sm font-semibold text-foreground">{title}</h3> : null}
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
    ) : null;

  const footer = sourceNote ? (
    <p className="text-xs text-muted-foreground/70">{sourceNote}</p>
  ) : null;

  if (!renderable) {
    return (
      <div ref={ref} className={cn(chartVariants(), className)} data-slot="chart" {...rest}>
        {header}
        <div
          className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border px-4 text-center text-sm text-muted-foreground"
          style={{ height }}
        >
          <span>{type === undefined ? "Can't render this chart." : "No data to chart."}</span>
          {/* The contract's graceful fallback: an empty / unknown-type chart
              still shows its accurate one-line summary (chart_block.py §6). */}
          {summary ? <span className="text-xs text-muted-foreground/80">{summary}</span> : null}
        </div>
        {footer}
      </div>
    );
  }

  // Legacy specs without a valueFormat keep raw recharts ticks/tooltips.
  const tickFormatter = valueFormat
    ? (value: unknown) => formatAxisTick(value, valueFormat, resolvedLocale)
    : undefined;
  const tooltipFormatter = valueFormat
    ? (value: unknown) => formatChartValue(value, valueFormat, resolvedLocale)
    : undefined;
  const categoryTickFormatter = (value: unknown) => truncateLabel(value);

  const tooltipStyle: React.CSSProperties = {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.5rem",
    color: "hsl(var(--popover-foreground))",
    fontSize: "0.75rem",
  };
  const axisProps = {
    stroke: AXIS_COLOR,
    tickLine: false,
    axisLine: false,
    tick: { fill: AXIS_COLOR, fontSize: 11 },
  } as const;
  const legendEl = showLegend ? <Legend wrapperStyle={{ fontSize: "0.75rem" }} /> : <></>;
  const gridEl = showGrid ? <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} /> : <></>;

  // Dense x-axes tilt their category labels so they stop colliding (v3
  // parity); wide charts scroll inside their own container so a dense plot
  // never forces the chat column to overflow.
  const dense = data.length > DENSE_LABEL_THRESHOLD;
  const denseAxisProps = dense
    ? ({ angle: -35, textAnchor: "end", height: 56 } as const)
    : ({ angle: 0, textAnchor: "middle", height: 30 } as const);
  const cartesianMargin = { top: 8, right: 16, bottom: dense ? 48 : 8, left: 8 };
  const plotMinWidth =
    data.length > SCROLL_ROW_THRESHOLD ? Math.min(data.length * 48, 1400) : undefined;

  function renderPlot(): React.ReactElement {
    if (type === "pie") {
      // Pie is single-measure: slice by the x label, values from the FIRST
      // series (lenient on multi-series, matching the BE's generic
      // validation). Slice colors resolve like v3's `seriesColor`: an
      // explicit series color wins, else the palette cycles per slice.
      // Donut = the same pie with a hole ~60% of the outer radius (48/80).
      const measure = series[0];
      return (
        <PieChart>
          <Tooltip contentStyle={tooltipStyle} formatter={tooltipFormatter} />
          {legendEl}
          <Pie
            data={data}
            dataKey={measure.key}
            nameKey={xKey || undefined}
            cx="50%"
            cy="50%"
            innerRadius={donut ? "48%" : 0}
            outerRadius="80%"
            stroke={SLICE_STROKE}
            isAnimationActive={false}
            label={(entry: unknown) => truncateLabel((entry as Record<string, unknown>)[xKey])}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={measure.color ?? palette[i % palette.length]} />
            ))}
          </Pie>
        </PieChart>
      );
    }

    if (type === "scatter") {
      // x from the axis key, y from each series - recharts wants the rows on
      // each <Scatter> and a dataKey on BOTH axes (v3 parity). `x.type`
      // "number" / "time" gives a numeric x-axis; anything else is category.
      // ZAxis pins a fixed dot size; bubble sizing is deliberately out of
      // scope.
      const xIsNumeric = xType === "number" || xType === "time";
      return (
        <ScatterChart margin={cartesianMargin}>
          {gridEl}
          <XAxis
            dataKey={xKey || undefined}
            type={xIsNumeric ? "number" : "category"}
            name={xLabel || xKey}
            {...axisProps}
            tickFormatter={
              xIsNumeric
                ? (value: unknown) => formatAxisTick(value, "float", resolvedLocale)
                : categoryTickFormatter
            }
            {...denseAxisProps}
          />
          <YAxis
            dataKey={series[0].key}
            type="number"
            name={series.length === 1 ? series[0].label : undefined}
            {...axisProps}
            tickFormatter={tickFormatter}
          />
          <ZAxis range={[60, 60]} />
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={{ strokeDasharray: "3 3", stroke: GRID_COLOR }}
            formatter={tooltipFormatter}
          />
          {legendEl}
          {series.map((s, i) => (
            <Scatter
              key={s.key}
              name={s.label}
              data={data}
              dataKey={s.key}
              fill={seriesColors[i]}
              isAnimationActive={false}
            />
          ))}
        </ScatterChart>
      );
    }

    if (type === "bar") {
      const horizontal = orientation === "horizontal";
      const barRadius: [number, number, number, number] = horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0];
      // recharts models a horizontal bar chart as layout="vertical" with the
      // category axis on Y and the value axis on X.
      const categoryAxis = horizontal ? (
        <YAxis
          dataKey={xKey || undefined}
          type="category"
          // Wider than the 60px default so the category labels horizontal
          // bars exist for (top-N names) don't truncate immediately.
          width={110}
          {...axisProps}
          tickFormatter={categoryTickFormatter}
        />
      ) : (
        <XAxis
          dataKey={xKey || undefined}
          {...axisProps}
          tickFormatter={categoryTickFormatter}
          {...denseAxisProps}
        />
      );
      const valueAxis = horizontal ? (
        <XAxis type="number" {...axisProps} tickFormatter={tickFormatter} />
      ) : (
        <YAxis {...axisProps} tickFormatter={tickFormatter} />
      );
      return (
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ ...cartesianMargin, bottom: dense && !horizontal ? 48 : 8 }}
        >
          {gridEl}
          {categoryAxis}
          {valueAxis}
          <Tooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
            formatter={tooltipFormatter}
          />
          {legendEl}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={seriesColors[i]}
              // Same stackId stacks the series; none renders multi-series
              // grouped side-by-side (the recharts default).
              stackId={stacked ? "stack" : undefined}
              radius={barRadius}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      );
    }

    if (type === "area") {
      return (
        <AreaChart data={data} margin={cartesianMargin}>
          {gridEl}
          <XAxis
            dataKey={xKey || undefined}
            {...axisProps}
            tickFormatter={categoryTickFormatter}
            {...denseAxisProps}
          />
          <YAxis {...axisProps} tickFormatter={tickFormatter} />
          <Tooltip contentStyle={tooltipStyle} formatter={tooltipFormatter} />
          {legendEl}
          {series.map((s, i) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              // The contract's `stacked` applies to multi-series area too.
              stackId={stacked ? "stack" : undefined}
              stroke={seriesColors[i]}
              fill={seriesColors[i]}
              fillOpacity={0.2}
              strokeWidth={2}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      );
    }

    return (
      <LineChart data={data} margin={cartesianMargin}>
        {gridEl}
        <XAxis
          dataKey={xKey || undefined}
          {...axisProps}
          tickFormatter={categoryTickFormatter}
          {...denseAxisProps}
        />
        <YAxis {...axisProps} tickFormatter={tickFormatter} />
        <Tooltip contentStyle={tooltipStyle} formatter={tooltipFormatter} />
        {legendEl}
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={seriesColors[i]}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    );
  }

  return (
    <div ref={ref} className={cn(chartVariants(), className)} data-slot="chart" {...rest}>
      {header}
      <div
        className="w-full overflow-x-auto"
        // The contract's mandatory `summary` is the chart's accessible
        // description - expose the plot as one labeled image region.
        role={summary ? "img" : undefined}
        aria-label={summary}
      >
        <div style={{ height, minWidth: plotMinWidth }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderPlot()}
          </ResponsiveContainer>
        </div>
      </div>
      {footer}
    </div>
  );
});

Chart.displayName = "Chart";

export { chartVariants };
export type ChartVariantProps = VariantProps<typeof chartVariants>;
